'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { Button } from '@repo/ui'
import { Close } from '@repo/ui/icons'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

/**
 * TableNoFilterResults container variants
 */
const tableNoFilterResultsVariants = cva(
  ['gap-sm flex flex-col items-center', 'w-full', 'px-[10px] py-[60px]'],
  {
    variants: {
      withBackground: {
        true: 'bg-card rounded-lg',
        false: 'bg-transparent'
      },
      withBorder: {
        true: 'border-[0.5px] border-border rounded-lg',
        false: ''
      },
      justify: {
        center: 'justify-center',
        start: 'justify-start',
        end: 'justify-end'
      }
    },
    defaultVariants: {
      withBackground: false,
      withBorder: false,
      justify: 'center'
    }
  }
)

export interface TableNoFilterResultsProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof tableNoFilterResultsVariants> {
  /** Type of entity being filtered */
  type: 'vaults' | 'markets' | 'curators' | 'transactions'
  /** Number of items hidden by filters */
  count?: number
  /** Whether filters are currently applied */
  hasFilters?: boolean
  /** Callback to clear filters */
  clearFilters: () => void
  /** Custom message replacing the default "No {type} matching the filters" text when filtered */
  message?: string
  /** Minimum height of the container */
  minHeight?: string | number
  /** Height of the container */
  height?: string
  /** Render prop for explore networks link (app-specific) */
  renderExploreLink?: ReactNode
}

/**
 * TableNoFilterResults component - Tailwind replica of dapp-ui-v5 TableNoFilterResults
 *
 * A context-aware empty state for filtered table results.
 * Shows either a "clear filters" button when filters are applied,
 * or an explore link when no items exist.
 *
 * @example
 * ```tsx
 * <TableNoFilterResults
 *   type="vaults"
 *   count={5}
 *   hasFilters
 *   clearFilters={() => setFilters({})}
 * />
 *
 * <TableNoFilterResults
 *   type="markets"
 *   clearFilters={() => {}}
 *   renderExploreLink={<ButtonLink href="/explore">Explore</ButtonLink>}
 * />
 * ```
 */
const TableNoFilterResults = forwardRef<HTMLDivElement, TableNoFilterResultsProps>(
  (
    {
      type,
      count,
      hasFilters = false,
      clearFilters,
      message,
      withBackground,
      withBorder,
      minHeight,
      height,
      justify,
      renderExploreLink,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const isFiltered = !!count || hasFilters

    return (
      <div
        ref={ref}
        data-testid="table-no-filter-results"
        className={cn(
          tableNoFilterResultsVariants({ withBackground, withBorder, justify }),
          className
        )}
        style={{ minHeight, height, ...style }}
        {...props}
      >
        <span className="text-secondary-foreground text-center text-sm">
          {isFiltered
            ? (message ?? `No ${type} matching the filters`)
            : `There are currently no ${type} on this network.`}
        </span>

        <div className="gap-md flex flex-wrap items-center justify-center md:flex-row">
          {isFiltered && (
            <>
              {!!count && (
                <span className="text-3xs">
                  <span className="text-foreground">
                    {count} Morpho {type}
                  </span>
                  <span className="text-secondary-foreground"> hidden by filters</span>
                </span>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={clearFilters}
                data-testid="clear-filters-button"
              >
                Clear filters
                <Close />
              </Button>
            </>
          )}
          {!isFiltered && type !== 'transactions' && renderExploreLink}
        </div>
      </div>
    )
  }
)

TableNoFilterResults.displayName = 'TableNoFilterResults'

export { TableNoFilterResults, tableNoFilterResultsVariants }
